import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import api from '../api/client';
import './LiveData.css';

interface LapData {
  lapTime: number;
  sector1: number;
  sector2: number;
  sector3: number;
  hasCollision: boolean;
}

interface DriverEntry {
  position?: number;
  driverName?: string;
  carName?: string;
  carNumber?: string;
  bestLapMs?: number;
  lastLapMs?: number;
  lapsCompleted?: number;
  pitStops?: number;
  status?: string;
  disconnected?: boolean;
  hasCollision?: boolean;
  collisionDetails?: { withWhat: string; hasDamage: boolean; otherDriver?: string };
  laps?: LapData[];
}

interface SessionInfo {
  code: string;
  label: string;
  hasStarted: boolean;
  hasEnded: boolean;
}

interface LiveDataResponse {
  serverId: string;
  sessionCode: string | null;
  sessionLabel: string;
  progressPercent: number;
  timeLeftSeconds?: number;
  sessionEnded?: boolean;
  allSessions?: SessionInfo[];
  liveData: DriverEntry[];
}

interface LiveDataProps {
  serverId: string;
  serverStatus?: string;
}

const formatMs = (ms?: number): string => {
  if (ms == null || ms <= 0) return '—';
  const totalSeconds = ms / 1000;
  const mins = Math.floor(totalSeconds / 60);
  const secs = (totalSeconds % 60).toFixed(3).padStart(6, '0');
  return `${mins}:${secs}`;
};

const formatTimeLeft = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')} left`;
};

const LiveData: React.FC<LiveDataProps> = ({ serverId, serverStatus }) => {
  const [data, setData] = useState<LiveDataResponse | null>(null);
  const [error, setError] = useState('');
  const [expandedDrivers, setExpandedDrivers] = useState<Set<string>>(new Set());

  const fetchLiveData = useCallback(async () => {
    try {
      const res = await api.getServerLiveData(serverId);
      setData(res.data);
      setError('');
    } catch {
      setError('Failed to load live session data.');
    }
  }, [serverId]);

  useEffect(() => {
    fetchLiveData();
    const interval = setInterval(fetchLiveData, 3000);
    return () => clearInterval(interval);
  }, [fetchLiveData]);

  const toggleDriver = (key: string) => {
    setExpandedDrivers(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (error) return <div className="live-data-error">{error}</div>;
  if (!data) return <div className="live-data-loading">Loading…</div>;

  const isRace = data.sessionCode === 'R';
  const hasDrivers = data.liveData.length > 0;
  const colCount = isRace ? 11 : 9;

  return (
    <div className="live-data">
      <div className="live-session-bar">
        <div className="live-session-pills">
          {data.allSessions?.map(s => (
            <span
              key={s.code}
              className={`live-pill ${s.code === data.sessionCode ? 'active' : ''} ${!s.hasStarted ? 'upcoming' : s.hasEnded ? 'ended' : ''}`}
            >
              {s.label}
            </span>
          ))}
        </div>
        <span className="live-session-label">
          {data.sessionLabel}
          {data.sessionEnded && <span className="live-ended-tag"> · ENDED</span>}
        </span>
        {data.timeLeftSeconds != null && !data.sessionEnded && (
          <span className="live-time-left">{formatTimeLeft(data.timeLeftSeconds)}</span>
        )}
        <div className="live-progress-bar">
          <div className="live-progress-fill" style={{ width: `${data.progressPercent}%` }} />
        </div>
      </div>

      {!hasDrivers ? (
        <div className="live-empty">No drivers on track yet.</div>
      ) : (
        <div className="live-table-wrap">
          <table className="live-table">
            <thead>
              <tr>
                <th className="col-expand"></th>
                <th className="col-pos">Pos</th>
                <th className="col-driver">Driver</th>
                <th className="col-num">#</th>
                <th className="col-car">Car</th>
                <th className="col-best">Best</th>
                <th className="col-last">Last</th>
                {isRace && <><th className="col-laps">Laps</th><th className="col-pits">Pits</th></>}
                <th className="col-status">Status</th>
                <th className="col-ev"></th>
              </tr>
            </thead>
            <tbody>
              {data.liveData.map((entry, idx) => {
                const key = entry.driverName ?? `driver-${idx}`;
                const expanded = expandedDrivers.has(key);
                const hasLaps = (entry.laps?.length ?? 0) > 0;
                return (
                  <React.Fragment key={key}>
                    <tr
                      className={`driver-row${entry.disconnected ? ' row-left' : ''}${expanded ? ' row-expanded' : ''}`}
                      onClick={() => hasLaps && toggleDriver(key)}
                      style={{ cursor: hasLaps ? 'pointer' : 'default' }}
                    >
                      <td className="col-expand">
                        {hasLaps && (expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
                      </td>
                      <td className="col-pos">{entry.position ?? idx + 1}</td>
                      <td className="col-driver">{entry.driverName || '—'}</td>
                      <td className="col-num">{entry.carNumber || '—'}</td>
                      <td className="col-car">{entry.carName || '—'}</td>
                      <td className="col-best">{formatMs(entry.bestLapMs)}</td>
                      <td className="col-last">{formatMs(entry.lastLapMs)}</td>
                      {isRace && (
                        <>
                          <td className="col-laps">{entry.lapsCompleted ?? '—'}</td>
                          <td className="col-pits">{entry.pitStops ?? 0}</td>
                        </>
                      )}
                      <td className="col-status">{entry.status || 'On Track'}</td>
                      <td className="col-ev">
                        {entry.hasCollision && (
                          <span
                            className="live-collision"
                            title={
                              entry.collisionDetails
                                ? `Collision with ${entry.collisionDetails.withWhat}${entry.collisionDetails.hasDamage ? ' · damaged' : ''}`
                                : 'Collision'
                            }
                          >
                            <AlertTriangle size={13} />
                          </span>
                        )}
                      </td>
                    </tr>
                    {expanded && hasLaps && (
                      <tr className="laps-row">
                        <td colSpan={colCount} className="laps-cell">
                          <table className="laps-table">
                            <thead>
                              <tr>
                                <th>Lap</th>
                                <th>Time</th>
                                <th>S1</th>
                                <th>S2</th>
                                <th>S3</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {entry.laps!.map((lap, lapIdx) => (
                                <tr key={lapIdx} className={lap.hasCollision ? 'lap-collision' : ''}>
                                  <td>{lapIdx + 1}</td>
                                  <td>{formatMs(lap.lapTime)}</td>
                                  <td>{formatMs(lap.sector1 || undefined)}</td>
                                  <td>{formatMs(lap.sector2 || undefined)}</td>
                                  <td>{formatMs(lap.sector3 || undefined)}</td>
                                  <td>
                                    {lap.hasCollision && <AlertTriangle size={12} className="live-collision" />}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LiveData;
